import { useState, useRef, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Badge } from '@safetyhub/ui';
import { useAuthStore } from '../stores/auth';
import { usePermissions } from '../hooks/usePermissions';
import { useGroupStore } from '../stores/group';
import type { Group } from '../lib/groups';
import { CheckIcon, ChevronDownIcon } from './icons';

interface GroupSelectorProps {
  groups: Group[];
}

/**
 * 그룹 선택기
 *
 * 가시 범위:
 * - Owner: 회사 전체 그룹 + "전체"(=필터 없음) + 관리 메뉴
 * - 비Owner: 자기가 속한 그룹만(getMe의 `groupMembers` 기준). 1개면 그 그룹으로 고정하고
 *   셀렉터를 숨기며, 여러 개면 "내 그룹 전체"(=미지정)가 기본
 *
 * 선택값은 뷰 필터일 뿐 권한이 아니다 — 비소속 그룹 지정은 서버가 403으로 막는다
 * (결정 기록 `2026-08-16-group-filter-and-visibility-enforcement`).
 */
export default function GroupSelector({ groups }: GroupSelectorProps) {
  const navigate = useNavigate();
  const { currentMembership } = useAuthStore();
  const { currentGroupId, setCurrentGroupId } = useGroupStore();
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // 소유자 성질(company-rbac P4) — 「전체」 항목·관리 메뉴는 owner 화면이지 권한 key가 아니다
  const { isOwner } = usePermissions();
  const groupMembers = currentMembership?.groupMembers;

  // 비Owner가 고를 수 있는 그룹. groupMembers 자체가 없는 응답(구버전 토큰 등)은 좁힐 근거가
  // 없다는 뜻이라 회사 목록을 그대로 둔다 — 실제 차단은 서버 스코프가 한다.
  const visibleGroups = useMemo(
    () =>
      isOwner || !groupMembers
        ? groups
        : groups.filter((g) => groupMembers.some((m) => m.groupId === g.id)),
    [isOwner, groupMembers, groups],
  );

  // 소속이 하나뿐인 비Owner는 그 그룹이 곧 유일한 범위 — 선택지가 아니라 고정값이다.
  const forcedGroupId =
    !isOwner && groupMembers && visibleGroups.length === 1 ? visibleGroups[0].id : null;

  // 현재 선택된 그룹
  const currentGroup = visibleGroups.find((g) => g.id === currentGroupId);

  const displayName = currentGroupId
    ? (currentGroup?.name ?? '그룹 선택')
    : isOwner
    ? '전체'
    : '내 그룹 전체';

  const handleSelect = (groupId: string | null) => {
    setCurrentGroupId(groupId);
    setIsOpen(false);
  };

  // 외부 클릭 감지 — hooks를 조기 return 이전에 선언해야 함
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // 선택값 보정 — localStorage에 남은 값이 지금 볼 수 있는 그룹이 아닐 수 있다(그룹에서 빠졌거나
  // 다른 회사에서 고른 값). 셀렉터가 숨는 경우에도 필요하므로 조기 return 이전에 둔다.
  useEffect(() => {
    if (groups.length === 0) return; // 멤버십 로드 전 — 판단 근거 없음
    if (forcedGroupId) {
      if (currentGroupId !== forcedGroupId) setCurrentGroupId(forcedGroupId);
      return;
    }
    // Owner도 검사 대상 — visibleGroups=회사 전체 그룹이라, 여기 없는 id는 타사에서 넘어온
    // 값이다. Owner를 건너뛰면 회사 전환 후 잔존 id가 5개 메뉴를 403으로 막는다.
    if (currentGroupId && !visibleGroups.some((g) => g.id === currentGroupId)) {
      setCurrentGroupId(null);
    }
  }, [groups.length, forcedGroupId, currentGroupId, visibleGroups, setCurrentGroupId]);

  // 고를 것이 없으면(그룹 1개 이하 또는 소속 1개 고정) 숨긴다
  if (visibleGroups.length < 2) {
    return null;
  }

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        // 작업공간 셀렉터(OrganizationSelector)와 같은 시각 언어 — 바로 아래 붙어 있어 hover 방향이
        // 다르면 튄다. 시안 `.ws`: soft 면 + 옅은 테두리, hover에 흰색 lift.
        className="w-full flex items-center justify-between px-3 py-[11px] text-left bg-surface-soft border border-line hover:bg-white hover:border-ink-300 rounded-card transition-colors"
      >
        <div className="flex-1 min-w-0">
          {/* 작업공간 셀렉터와 동일한 시안 `.ws` 타이포 — 바로 아래 붙어 있어 어긋나면 튄다 */}
          <p className="text-[13.5px] font-medium leading-tight text-ink-500">현재 그룹</p>
          <p className="text-base font-bold leading-tight tracking-[-0.01em] text-ink-900 truncate">
            {displayName}
          </p>
        </div>
        <ChevronDownIcon
          className={`h-4 w-4 flex-shrink-0 text-ink-400 transition-transform ${isOpen ? 'rotate-180' : ''}`}
        />
      </button>

      {isOpen && (
        <div className="absolute mt-1 left-0 right-0 bg-white rounded-card shadow-card border border-line py-1 z-50 max-h-72 overflow-y-auto">
          {/* 미지정(필터 없음) 옵션 — Owner는 회사 전체, 비Owner는 자기 그룹 전체를 뜻한다 */}
          <button
            onClick={() => handleSelect(null)}
            className={`w-full px-3 py-2 text-left hover:bg-surface-hover flex items-center ${
              !currentGroupId ? 'bg-brand-50' : ''
            }`}
          >
            {!currentGroupId && (
              <CheckIcon className="h-4 w-4 text-brand-600 mr-2 flex-shrink-0" />
            )}
            <GroupIcon className={`w-4 h-4 mr-2 flex-shrink-0 ${!currentGroupId ? 'text-brand-600' : 'text-ink-400'}`} />
            <span className={`text-sm ${!currentGroupId ? 'font-medium text-brand-700' : 'text-ink-700'}`}>
              {isOwner ? '전체' : '내 그룹 전체'}
            </span>
          </button>

          {/* 그룹 목록 */}
          {visibleGroups.map((group) => {
            const isSelected = group.id === currentGroupId;
            return (
              <button
                key={group.id}
                onClick={() => handleSelect(group.id)}
                className={`w-full px-3 py-2 text-left hover:bg-surface-hover flex items-center justify-between ${
                  isSelected ? 'bg-brand-50' : ''
                }`}
              >
                <div className="flex items-center min-w-0">
                  {isSelected && (
                    <CheckIcon className="h-4 w-4 text-brand-600 mr-2 flex-shrink-0" />
                  )}
                  <GroupIcon className={`w-4 h-4 mr-2 flex-shrink-0 ${isSelected ? 'text-brand-600' : 'text-ink-400'}`} />
                  <span className={`text-sm truncate ${isSelected ? 'font-medium text-brand-700' : 'text-ink-700'}`}>
                    {group.name}
                  </span>
                </div>
                {group.isDefault && (
                  <Badge tone="slate" shape="rounded" className="ml-2 flex-shrink-0">
                    기본
                  </Badge>
                )}
              </button>
            );
          })}

          {/* Owner만 새 그룹 + 관리 옵션 */}
          {isOwner && (
            <div className="border-t border-line mt-1 pt-1">
              <button
                onClick={() => {
                  setIsOpen(false);
                  // TODO: Phase 6 — 새 그룹 생성 모달 연결
                  navigate('/settings/groups');
                }}
                className="w-full px-3 py-2 text-left hover:bg-surface-hover flex items-center text-sm text-brand-600"
              >
                <span className="text-base mr-2 leading-none">+</span>
                새 그룹 만들기
              </button>
              <button
                onClick={() => {
                  setIsOpen(false);
                  navigate('/settings/groups');
                }}
                className="w-full px-3 py-2 text-left hover:bg-surface-hover flex items-center text-sm text-ink-600"
              >
                <CogIcon className="w-4 h-4 mr-2 text-ink-400" />
                그룹 관리
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function GroupIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"
      />
    </svg>
  );
}

function CogIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z"
      />
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
      />
    </svg>
  );
}
